import { AttachmentBadge } from '../../../shared/ui/feedback/AttachmentBadge'
import { resolveRunningMarker, type WorkFit } from '../model/blockDensity'
import { META_CLASS, SUBJECT_CLASS, TIME_CLASS } from './BlockSegment'

/**
 * What the work segment says, at the size it happens to be.
 *
 * Shared by Termin and Aufgabe, which differ only in what they call the subject. The rows are handed
 * out from the count `resolveWorkDensity` measured, cheapest first, and a row that was not paid for
 * is not drawn — that, and nothing about overflow, is what stops the address being sliced in half.
 *
 * Order of spending: the time always, then the subject, then who to ask for, then how to reach them.
 * A worker looking at this on a phone between two jobs wants when and where before who.
 */

interface Props {
  fit: WorkFit
  timeLabel: string
  /** The location for a Termin, the title for an Aufgabe. */
  subject: string | null
  /** Usually the tenant's name. */
  who?: string | null
  /** Usually the tenant's phone number. */
  contact?: string | null
  running?: boolean
  attachmentCount?: number
}

/** The running marker, inline and after the time so a narrow block truncates the badge, not the time. */
const RunningMarker = ({ withWord }: { withWord: boolean }) => (
  <span className="inline-flex shrink-0 items-center gap-1 rounded bg-red-600 px-1 py-px text-[9px] font-bold uppercase leading-none text-white">
    <span className="h-1 w-1 shrink-0 animate-pulse rounded-full bg-white" />
    {withWord && <span>Läuft</span>}
  </span>
)

export const WorkBody = ({
  fit,
  timeLabel,
  subject,
  who,
  contact,
  running = false,
  attachmentCount = 0,
}: Props) => {
  const { tier, lines } = fit

  // Nothing fits. The block's colour, its pulsing ring and its `aria-label` carry it from here.
  if (tier === 'bare') return null

  const marker = resolveRunningMarker(tier)

  if (tier === 'single') {
    // One row, two facts. Kept as two elements rather than one joined string: the time is fixed-width
    // and must not be the thing that truncates, and the subject is what tests and search look for.
    return (
      <span className="flex min-w-0 items-center gap-1">
        <span className={`${TIME_CLASS} shrink-0`}>{timeLabel}</span>
        {running && marker !== 'ring' && <RunningMarker withWord={false} />}
        {subject && <span className={SUBJECT_CLASS}>{subject}</span>}
      </span>
    )
  }

  // Rows are spent in order; each `lines >=` gate is one more row the block actually has.
  const showWho = Boolean(who) && lines >= 3
  const showContact = Boolean(contact) && lines >= 4
  const spent = 1 + (subject ? 1 : 0) + (showWho ? 1 : 0) + (showContact ? 1 : 0)
  // A row nobody claimed goes back to the subject — a long street name is the thing most likely to
  // have been cut, and the block that has a row to spare is exactly the one that can afford it.
  const subjectClamped = Boolean(subject) && lines - spent >= 1

  return (
    <div className="flex h-full w-full min-w-0 flex-col justify-start">
      <span className="flex min-w-0 items-center gap-1">
        <span className={`${TIME_CLASS} shrink-0`}>{timeLabel}</span>
        {running && marker !== 'ring' && <RunningMarker withWord={marker === 'pill' && lines >= 4} />}
        {attachmentCount > 0 && (
          <span className="ml-auto shrink-0">
            <AttachmentBadge count={attachmentCount} inline />
          </span>
        )}
      </span>

      {subject && (
        <span
          className={
            subjectClamped
              ? 'line-clamp-2 break-words text-[12px] font-semibold leading-[13px]'
              : SUBJECT_CLASS
          }
        >
          {subject}
        </span>
      )}

      {showWho && <span className={META_CLASS}>{who}</span>}
      {showContact && <span className={`${META_CLASS} tabular-nums`}>{contact}</span>}
    </div>
  )
}
